import React, { useEffect, useState } from 'react';
import { Table, Spinner, Alert, Card, Accordion, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  ChatDots,
  Star,
  CalendarDate,
  PersonBadge,
  EnvelopeFill,
  ExclamationTriangle,
  QuestionCircle,
  ClockHistory,
  ArrowLeft,
  ArrowDown
} from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from "@mui/material";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import API_BASE_URL from '../../../config/api';

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const FeedbackPage = () => {
  const { classId } = useParams();
  const [feedbacks, setFeedbacks] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qnaLoading, setQnaLoading] = useState(true);
  const [incidentLoading, setIncidentLoading] = useState(true);
  const [error, setError] = useState('');
  const [qnaError, setQnaError] = useState('');
  const [incidentError, setIncidentError] = useState('');

  const fetchFeedbacks = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/feedback/getAllFeedbacks/${classId}`);
      const feedbacksData = res.data.data || [];
      setFeedbacks(feedbacksData);
      
      if (feedbacksData.length > 0 && feedbacksData[0].sessionId) {
        setSessionId(feedbacksData[0].sessionId);
        return feedbacksData[0].sessionId;
      }
      return null;
    } catch (err) {
      setError('Failed to load feedbacks');
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionnaires = async () => {
    try {
      setQnaLoading(true);
      const feedbacksRes = await axios.get(`${API_BASE_URL}/api/feedback/getAllFeedbacks/${classId}`);
      const userIds = [...new Set(feedbacksRes.data.data.map(fb => fb.user?._id).filter(id => id))];
      
      const questionnairePromises = userIds.map(userId => 
        axios.get(`${API_BASE_URL}/api/questionaire/getUserQuestionnaireWithAnswers/${classId}/${userId}`)
      );
      
      const questionnaireResults = await Promise.allSettled(questionnairePromises);
      
      const validQuestionnaires = questionnaireResults
        .filter(result => result.status === 'fulfilled' && result.value.data.success)
        .map(result => {
          const data = result.value.data.data;
          return {
            userId: data.userId,
            classId: data.classId,
            sessionId: data.sessionId,
            questions: data.questions || [],
            userName: feedbacksRes.data.data.find(fb => fb.user?._id === data.userId)?.user?.name || 'Unknown User'
          };
        });
      
      setQuestionnaires(validQuestionnaires);
    } catch (err) {
      setQnaError('Failed to load questions and answers');
      console.error(err);
    } finally {
      setQnaLoading(false);
    }
  };

  const fetchIncidents = async (sessionId) => {
    try {
      setIncidentLoading(true);
      
      if (!sessionId) {
        setIncidentError('Session ID is not available');
        setIncidentLoading(false);
        return;
      }
      
      const res = await axios.get(`${API_BASE_URL}/api/incident/getIncidentBySession/${sessionId}`);
      setIncidents(res.data.data || []);
    } catch (err) {
      setIncidentError('Failed to load incidents');
      console.error(err);
    } finally {
      setIncidentLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        const sessionIdFromFeedbacks = await fetchFeedbacks();
        await fetchQuestionnaires();
        
        if (sessionIdFromFeedbacks) {
          await fetchIncidents(sessionIdFromFeedbacks);
        } else {
          setIncidentError('No session ID found in feedback data');
          setIncidentLoading(false);
        }
      } catch (err) {
        console.error('Error initializing data:', err);
      }
    };

    initializeData();
  }, [classId]);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Link underline="hover" color="inherit" href="/data/classses">
          Classes
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Class Feedback
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <ChatDots size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Class Feedback
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Session ID: {sessionId || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          gap={2}
          py={8}
          sx={{ background: "#fff", borderRadius: "14px", border: "1px solid #e4eaf8" }}
        >
          <Spinner
            animation="border"
            style={{
              color: "#0d47a1",
              width: 36,
              height: 36,
              borderWidth: 3,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Loading feedback data...
          </Typography>
        </Box>
      ) : error ? (
        <Alert variant="danger" style={{ borderRadius: '12px' }}>{error}</Alert>
      ) : (
        <>
          {/* Feedbacks Table */}
          <Paper
            sx={{
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e4eaf8",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
              mb: 3
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #e4eaf8', background: '#fafbff' }}>
              <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>
                <ChatDots size={16} style={{ marginRight: '8px', marginTop: '-2px' }} />
                Feedback Comments
              </Typography>
            </Box>
            <Table hover className="mb-0 align-middle">
              <thead style={{ background: primaryGradient }}>
                <tr>
                  {["#", "Comment", "Rating", "Date"].map((h) => (
                    <th
                      key={h}
                      style={{
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        padding: "13px 16px",
                        border: "none",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {feedbacks.length > 0 ? feedbacks.map((fb, idx) => (
                  <tr
                    key={fb._id || idx}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {fb.feedback || 'No comment'}
                    </td>
                    <td>
                      {fb.rating ? (
                        <span
                          className="badge px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                          style={{
                            background: '#e4eaf8',
                            color: '#1a237e',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          <Star size={12} color="#f57f17" /> {fb.rating}/5
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      <ClockHistory size={12} className="me-1" />
                      {new Date(fb.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">
                      No feedbacks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Paper>

          {/* Questions and Answers Section */}
          <Paper
            sx={{
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e4eaf8",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
              mb: 3
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #e4eaf8', background: '#fafbff' }}>
              <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>
                <QuestionCircle size={16} style={{ marginRight: '8px', marginTop: '-2px' }} />
                Questions and Answers
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {qnaLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <Spinner animation="border" style={{ color: "#0d47a1" }} />
                </Box>
              ) : qnaError ? (
                <Alert variant="warning">{qnaError}</Alert>
              ) : questionnaires.length === 0 ? (
                <Typography variant="caption" sx={{ color: "#7b8ab8", display: 'block', textAlign: 'center', py: 3 }}>
                  No questions and answers available.
                </Typography>
              ) : (
                <Box>
                  {questionnaires.map((qna, idx) => (
                    <MuiAccordion
                      key={idx}
                      sx={{
                        mb: 1,
                        border: '1px solid #e4eaf8',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        boxShadow: 'none'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ArrowDown />}
                        sx={{
                          backgroundColor: '#f8f9ff',
                          borderRadius: '8px',
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            gap: 2
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonBadge size={16} color="#1a237e" />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a237e' }}>
                            {qna.userName}
                          </Typography>
                          <Chip
                            label={`${qna.questions.length} Questions`}
                            size="small"
                            sx={{
                              backgroundColor: '#e4eaf8',
                              color: '#1a237e',
                              fontSize: '0.7rem',
                              height: '20px'
                            }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        {qna.questions.length > 0 ? (
                          qna.questions.map((question, qIdx) => (
                            <Box
                              key={qIdx}
                              sx={{
                                mb: 2,
                                p: 1.5,
                                backgroundColor: '#f8f9ff',
                                borderRadius: '8px',
                                border: '1px solid #e4eaf8'
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0d47a1', display: 'block', mb: 0.5 }}>
                                Q: {question.questionText}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6b7a99', display: 'block', ml: 1 }}>
                                <strong>A:</strong> {question.answerText || 'No answer provided'}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                            No questions answered by this user.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </MuiAccordion>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>

          {/* Incidents Section */}
          <Paper
            sx={{
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e4eaf8",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #e4eaf8', background: '#fafbff' }}>
              <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>
                <ExclamationTriangle size={16} style={{ marginRight: '8px', marginTop: '-2px' }} />
                Incidents
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {incidentLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <Spinner animation="border" style={{ color: "#0d47a1" }} />
                </Box>
              ) : incidentError ? (
                <Alert variant="warning">{incidentError}</Alert>
              ) : incidents.length === 0 ? (
                <Typography variant="caption" sx={{ color: "#7b8ab8", display: 'block', textAlign: 'center', py: 3 }}>
                  No incidents recorded.
                </Typography>
              ) : (
                <Table hover className="mb-0 align-middle">
                  <thead style={{ background: primaryGradient }}>
                    <tr>
                      {["#", "Instructor", "Description", "Date"].map((h) => (
                        <th
                          key={h}
                          style={{
                            color: "rgba(255,255,255,0.85)",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            padding: "13px 16px",
                            border: "none",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incidents.map((incident, idx) => (
                      <tr
                        key={incident._id || idx}
                        style={{ borderBottom: "1px solid #f0f4ff" }}
                      >
                        <td
                          style={{
                            color: "#aab4cc",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "#1a237e", display: 'block' }}
                            >
                              {incident.instructor?.name || 'N/A'}
                            </Typography>
                            {incident.instructor?.email && (
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.7rem", color: "#7b8ab8" }}
                              >
                                {incident.instructor.email}
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                          {incident.description || 'No description'}
                        </td>
                        <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                          <ClockHistory size={12} className="me-1" />
                          {incident.createdAt ? new Date(incident.createdAt).toLocaleString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default FeedbackPage;